import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface CartProduct {
  id: string;
  name: string;
  price: number;
  original_price?: number | null;
  image?: string | null;
  slug: string;
  requires_file_upload?: boolean;
  requires_text_input?: boolean;
  stock_quantity?: number | null;
  max_order_quantity?: number | null;
}

interface CartItem {
  product: CartProduct;
  quantity: number;
  customerText?: string;
  customerFiles?: string[]; // storage URLs
}

interface CartStore {
  items: CartItem[];
  drawerOpen: boolean;
  lastAddedId: string | null;
  lastAddedQty: number;
  clearLastAdded: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  addItem: (product: CartProduct, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateCustomerText: (productId: string, text: string) => void;
  updateCustomerFiles: (productId: string, files: string[]) => void;
  clearCart: () => void;
  totalItems: () => number;
  totalPrice: () => number;
}

export const useCartStore = create<CartStore>()(persist((set, get) => ({
  items: [],
  drawerOpen: false,
  lastAddedId: null,
  lastAddedQty: 0,
  clearLastAdded: () => set({ lastAddedId: null, lastAddedQty: 0 }),
  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false, lastAddedId: null, lastAddedQty: 0 }),
  addItem: (product, quantity = 1) => {
    set((state) => {
      const existing = state.items.find((i) => i.product.id === product.id);
      const items = existing
        ? state.items.map((i) =>
            i.product.id === product.id ? { ...i, quantity: i.quantity + quantity } : i
          )
        : [...state.items, { product, quantity }];
      return { items, drawerOpen: true, lastAddedId: product.id, lastAddedQty: quantity };
    });
  },
  removeItem: (productId) => {
    set((state) => ({ items: state.items.filter((i) => i.product.id !== productId) }));
  },
  updateQuantity: (productId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(productId);
      return;
    }
    set((state) => ({
      items: state.items.map((i) =>
        i.product.id === productId ? { ...i, quantity } : i
      ),
    }));
  },
  updateCustomerText: (productId, text) => {
    set((state) => ({
      items: state.items.map((i) =>
        i.product.id === productId ? { ...i, customerText: text } : i
      ),
    }));
  },
  updateCustomerFiles: (productId, files) => {
    set((state) => ({
      items: state.items.map((i) =>
        i.product.id === productId ? { ...i, customerFiles: files } : i
      ),
    }));
  },
  clearCart: () => set({ items: [] }),
  totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
  totalPrice: () => get().items.reduce((sum, i) => sum + i.product.price * i.quantity, 0),
}), {
  name: 'cycy-cart-v1',
  storage: createJSONStorage(() => localStorage),
  partialize: (state) => ({ items: state.items }) as any,
}));
